import { Box, Button, Chip, Collapse, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { Component } from 'react';
import { MdClose, MdCreate, MdDescription, MdOutlineCalculate, MdOutlineInventory2, MdTrendingDown, MdTrendingUp, MdWarning } from 'react-icons/md';

import { aplicarDesconto, formatBRL } from "../util/apuracaoCalc.js";

const maskParaNumero = (texto) => {
    let digitos = String(texto).replace(/\D/g, '');
    return digitos ? parseInt(digitos, 10) / 100 : 0;
};

const apenasDigitos = (texto) => String(texto).replace(/\D/g, '');

export default class ApuracaoPanel1 extends Component {

    constructor(props) {
        super(props);
        this.state = {
            editando: false,
            // se os dados já vierem com um ajuste (ex: apuração fechada, veio do backend),
            // o painel já nasce com o resumo aberto mostrando ele
            ajuste: props.dados?.ajuste || null,
            descFaltasValor: 0,
            descFaltasItens: '',
            descSobrasValor: 0,
            descSobrasItens: '',
            justificativa: '',
            erro: ''
        };
    }

    componentDidUpdate(prevProps) {
        // a busca é assíncrona: o painel monta com dados={} e só depois recebe o valor real.
        // se chegou um ajuste que ainda não tínhamos no state, sincroniza.
        if (!prevProps.dados?.ajuste && this.props.dados?.ajuste && !this.state.ajuste) {
            this.setState({ ajuste: this.props.dados.ajuste });
        }
    }

    abrirEdicao = () => {
        let { ajuste } = this.state;
        this.setState({
            editando: true,
            descFaltasValor: ajuste?.faltasValor || 0,
            descFaltasItens: ajuste?.faltasItens ? String(ajuste.faltasItens) : '',
            descSobrasValor: ajuste?.sobrasValor || 0,
            descSobrasItens: ajuste?.sobrasItens ? String(ajuste.sobrasItens) : '',
            justificativa: ajuste?.justificativa || '',
            erro: ''
        });
    }

    fecharEdicao = () => {
        this.setState({ editando: false, erro: '' });
    }

    toggleEditar = () => {
        this.state.editando ? this.fecharEdicao() : this.abrirEdicao();
    }

    handleChangeMoeda = (campo) => (e) => {
        this.setState({ [campo]: maskParaNumero(e.target.value), erro: '' });
    }

    handleChangeItens = (campo) => (e) => {
        this.setState({ [campo]: apenasDigitos(e.target.value), erro: '' });
    }

    handleChangeJustificativa = (e) => {
        this.setState({ justificativa: e.target.value.slice(0, 1023), erro: '' });
    }

    validar = () => {
        let { descFaltasValor, descFaltasItens, descSobrasValor, descSobrasItens, justificativa } = this.state;
        let temFaltas = descFaltasValor > 0;
        let temSobras = descSobrasValor > 0;

        if (!temFaltas && !temSobras) {
            return 'Preencha ao menos um valor a desconsiderar (faltas ou sobras).';
        }
        if (temFaltas && (!descFaltasItens || parseInt(descFaltasItens) <= 0)) {
            return 'Informe a quantidade de itens de faltas desconsiderados.';
        }
        if (temSobras && (!descSobrasItens || parseInt(descSobrasItens) <= 0)) {
            return 'Informe a quantidade de itens de sobras desconsiderados.';
        }
        if (!justificativa.trim()) {
            return 'Justificativa é obrigatória.';
        }
        if (justificativa.length > 1023) {
            return 'Justificativa deve ter no máximo 1023 caracteres.';
        }
        return '';
    }

    handleAplicar = () => {
        let erro = this.validar();
        if (erro) {
            this.setState({ erro });
            return;
        }

        let ajuste = {
            faltasValor: this.state.descFaltasValor,
            faltasItens: parseInt(this.state.descFaltasItens) || 0,
            sobrasValor: this.state.descSobrasValor,
            sobrasItens: parseInt(this.state.descSobrasItens) || 0,
            justificativa: this.state.justificativa.trim()
        };

        this.setState({ ajuste, editando: false, erro: '' });

        if (this.props.onAplicarAjuste) {
            this.props.onAplicarAjuste(ajuste);
        }
    }

    removerAjuste = () => {
        this.setState({ ajuste: null });
        if (this.props.onAplicarAjuste) {
            this.props.onAplicarAjuste(null);
        }
    }

    render() {
        let { loading, tipo, dados, nome, disabled } = this.props;
        let { editando, ajuste, erro } = this.state;

        // valores exibidos nos cards = dados originais com o ajuste aplicado (se houver) —
        // regra de desconto vem do util compartilhado (apuracaoCalc.js)
        let faltasValor = aplicarDesconto(dados?.faltas?.valor || 0, ajuste?.faltasValor || 0);
        let faltasItens = (dados?.faltas?.itens || 0) - (ajuste?.faltasItens || 0);
        let sobrasValor = aplicarDesconto(dados?.sobras?.valor || 0, ajuste?.sobrasValor || 0);
        let sobrasItens = (dados?.sobras?.itens || 0) - (ajuste?.sobrasItens || 0);
        let totalValor = faltasValor + sobrasValor;
        let totalItens = faltasItens + sobrasItens;

        return (
            <>
                {loading ? <Skeleton height={182} variant="rounded" /> :
                    <Stack bgcolor="#f5f7fa" borderRadius={2} overflow={'hidden'}>
                        <Stack direction="row" bgcolor="#ebf1fa" height={40} color="#0A57A3" alignItems="center" gap={1} p={1.2}>
                            <MdOutlineInventory2 />
                            <Typography variant="caption"><b>{nome}</b></Typography>
                            <Box flex={1.0} />
                            <Stack direction="row" bgcolor="#0A57A3" color="white" alignItems="center" p={0.5} borderRadius={2}>
                                <Typography fontSize={10}>{tipo}</Typography>
                            </Stack>
                            {!disabled && <Box title="Editar este resultado" mt={-0.8}>
                                <IconButton onClick={this.toggleEditar}><MdCreate color="#0A57A3" size={15} /></IconButton>
                            </Box>
                            }
                        </Stack>
                        <Stack p={2} direction="row" gap={2}>
                            <Stack bgcolor="#fef2f2" color="#b91c1c" borderRadius={2} border="1px solid #e5e7eb" p={2} gap={1} flex={1}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <MdTrendingDown size={12} />
                                    <Typography fontSize={10}><b>FALTAS</b></Typography>
                                </Stack>
                                <Typography fontSize={18}><b>{formatBRL(faltasValor)}</b></Typography>
                                <Typography fontSize={12}>{faltasItens} itens</Typography>
                            </Stack>

                            <Stack bgcolor="#f0fdf4" color="#15803d" borderRadius={2} border="1px solid #e5e7eb" p={2} gap={1} flex={1}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <MdTrendingUp size={12} />
                                    <Typography fontSize={10}><b>SOBRAS</b></Typography>
                                </Stack>
                                <Typography fontSize={18}><b>{formatBRL(sobrasValor)}</b></Typography>
                                <Typography fontSize={12}>{sobrasItens} itens</Typography>
                            </Stack>

                            <Stack bgcolor="#f8f9fa" color="#136188" borderRadius={2} border="1px solid #e5e7eb" p={2} gap={1} flex={1}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <MdOutlineCalculate size={12} />
                                    <Typography fontSize={10}><b>TOTAL</b></Typography>
                                </Stack>
                                <Typography fontSize={18}><b>{formatBRL(totalValor)}</b></Typography>
                                <Typography fontSize={12}>{totalItens} itens</Typography>
                            </Stack>
                        </Stack>

                        <Collapse in={editando}>
                            <Box px={2} pb={2}>
                                <Stack bgcolor="#ffffff" border="1px solid #e5e7eb" borderRadius={2} p={2} gap={2}>
                                    <Stack direction="row" gap={2} flexWrap="wrap">
                                        <TextField
                                            label="Desconsiderar Faltas (R$)"
                                            size="small"
                                            placeholder="R$ 0,00"
                                            value={this.state.descFaltasValor ? formatBRL(this.state.descFaltasValor) : ''}
                                            onChange={this.handleChangeMoeda('descFaltasValor')}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={{ flex: 1, minWidth: 160 }}
                                        />
                                        <TextField
                                            label="Itens Faltas"
                                            size="small"
                                            placeholder="0"
                                            required={this.state.descFaltasValor > 0}
                                            value={this.state.descFaltasItens}
                                            onChange={this.handleChangeItens('descFaltasItens')}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={{ flex: 1, minWidth: 160 }}
                                        />
                                        <TextField
                                            label="Desconsiderar Sobras (R$)"
                                            size="small"
                                            placeholder="R$ 0,00"
                                            value={this.state.descSobrasValor ? formatBRL(this.state.descSobrasValor) : ''}
                                            onChange={this.handleChangeMoeda('descSobrasValor')}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={{ flex: 1, minWidth: 160 }}
                                        />
                                        <TextField
                                            label="Itens Sobras"
                                            size="small"
                                            placeholder="0"
                                            required={this.state.descSobrasValor > 0}
                                            value={this.state.descSobrasItens}
                                            onChange={this.handleChangeItens('descSobrasItens')}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={{ flex: 1, minWidth: 160 }}
                                        />
                                    </Stack>

                                    <TextField
                                        label="Justificativa"
                                        placeholder="Descreva o motivo para desconsiderar estes valores..."
                                        multiline
                                        minRows={2}
                                        size="small"
                                        required
                                        value={this.state.justificativa}
                                        onChange={this.handleChangeJustificativa}
                                        inputProps={{ maxLength: 1023 }}
                                        helperText={`${this.state.justificativa.length}/1023`}
                                    />

                                    {erro &&
                                        <Typography fontSize={12} color="#b91c1c">{erro}</Typography>
                                    }

                                    <Stack direction="row" gap={1}>
                                        <Button
                                            disableElevation
                                            variant="contained"
                                            size="small"
                                            startIcon={<MdCreate />}
                                            onClick={this.handleAplicar}
                                            sx={{ bgcolor: '#0A57A3', '&:hover': { bgcolor: '#084684' } }}
                                        >
                                            Aplicar
                                        </Button>
                                        <Button
                                            disableElevation
                                            variant="outlined"
                                            size="small"
                                            color="inherit"
                                            startIcon={<MdClose />}
                                            onClick={this.fecharEdicao}
                                        >
                                            Cancelar
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Collapse>

                        {ajuste && !editando &&
                            <Box px={2} pb={2}>
                                <Stack bgcolor="#fffbeb" border="1px solid #fde68a" borderRadius={2} p={2} gap={1.5}>
                                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                        <Stack direction="row" alignItems="center" gap={0.5} color="#b45309">
                                            <MdWarning size={14} />
                                            <Typography fontSize={12} fontWeight={700}>DESCONSIDERADO:</Typography>
                                        </Stack>
                                        {ajuste.faltasValor > 0 &&
                                            <Chip size="small" label={`Faltas desc.: ${formatBRL(ajuste.faltasValor)}`} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }} />
                                        }
                                        {ajuste.faltasItens > 0 &&
                                            <Chip size="small" label={`${ajuste.faltasItens} itens faltas desc.`} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }} />
                                        }
                                        {ajuste.sobrasValor > 0 &&
                                            <Chip size="small" label={`Sobras desc.: ${formatBRL(ajuste.sobrasValor)}`} sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                                        }
                                        {ajuste.sobrasItens > 0 &&
                                            <Chip size="small" label={`${ajuste.sobrasItens} itens sobras desc.`} sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                                        }
                                    </Stack>

                                    <Stack bgcolor="#ffffff" border="1px solid #e5e7eb" borderRadius={2} p={1.5} direction="row" gap={1}>
                                        <MdDescription color="#b45309" size={16} />
                                        <Stack>
                                            <Typography fontSize={11} fontWeight={700} color="#b45309">JUSTIFICATIVA</Typography>
                                            <Typography fontSize={12} color={ajuste.justificativa ? '#1f2937' : '#9ca3af'}>
                                                {ajuste.justificativa || '(nenhum)'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    {!disabled &&
                                        <Stack direction="row" gap={1}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<MdCreate />}
                                                onClick={this.abrirEdicao}
                                                sx={{ color: '#b45309', borderColor: '#fde68a', '&:hover': { borderColor: '#f59e0b', bgcolor: '#fef3c7' } }}
                                            >
                                                Editar ajuste
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<MdClose />}
                                                onClick={this.removerAjuste}
                                            >
                                                Remover ajuste
                                            </Button>
                                        </Stack>
                                    }
                                </Stack>
                            </Box>
                        }
                    </Stack>
                }
            </>
        );
    }
}
